import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Paper, Typography } from '@mui/material';
import { DIALOG_TYPE } from '../../../../constants';
import { ADMIN_PAGES_KEYS, NS } from '../../../../i18n/constants/keys';
import { Dialog } from '../../../../ui';
import { JobOpeningsContext } from '../../../../utils/contexts/JobOpening';
import { useCreateJobOpening } from '../Hooks/useCreateJobOpening';
import { CreateJobDialog } from '../components/CreateJobDialog';
import { JobOpeningBreadcrumb } from '../components/JobOpeningBreadcrumb';
import { JobOpeningTabs } from '../components/JobOpeningTabs';
import ProgressTracker from '../ui/ProgressTracker';
import LoadingSpinner from '../ui/LoaderSpinner';
import StatusMessage from '../JobOpeningDetails/StatusMessage';
import JobOpeningDetails from '../JobOpeningDetails';
import { AllApplicantAnalysis } from '../ApplicantAnalysis/AllApplicantAnalysis';
import AssessmentQuestions from '../AssessmentQuestions';
import { Interviews } from '../Interviews';
import { Psychometric } from '../Psychometric';
import { WellnessTab } from '../Wellness';

export const CreateJobOpeningContainer = () => {
  const { t } = useTranslation(NS.adminPages);

  const {
    // Form state
    jobRequisition,
    setJobRequisition,
    jobDescription,
    setJobDescription,
    autoSuitability,
    setAutoSuitability,
    autoOutstanding,
    setAutoOutstanding,
    autoAssessment,
    setAutoAssessment,
    jobPostingData,
    setJobPostingData,
    jobPostExist,

    // Job info
    jobTitleLabel,
    setJobTitleLabel,

    // Dialog state
    modalState,
    setModalState,
    confirmDialogState,
    setConfirmDialogState,

    // Navigation
    steps,
    currentPage,
    currentSubTab,
    setCurrentPage,
    navigateToSubTab,

    // Page status
    pageStatus,
    requiredTabs,
    enableElement,
    dataLoading,

    // Modal toggle
    handleModalToggle,

    // Progress tracker
    stages,
    setStages,

    // Elements & tabs
    jobOpeningElements,
    setJobOpeningElements,
    completedJobOpeningDetails,
    setCompletedJobOpeningDetails,

    // Progress tracker visibility
    show,
    setShow,

    // Tab counts
    tabCounts,

    // Job ID
    jobOpeningId,
  } = useCreateJobOpening();

  /** Render the active form page based on current tab */
  const renderFormPage = () => {
    if (dataLoading || pageStatus === 'loading') return <LoadingSpinner />;

    if (pageStatus !== 'enabled') {
      return (
        <StatusMessage
          status={pageStatus}
          requiredTabs={requiredTabs}
          enabledElement={enableElement}
          setCurrentPage={setCurrentPage}
          setStatusTab={navigateToSubTab}
        />
      );
    }

    switch (currentPage) {
      case 'Job Opening Details':
        return (
          <JobOpeningDetails
            tab={currentSubTab}
            stages={stages}
            setStages={setStages}
            jobTitleLabel={jobTitleLabel}
            setJobTitleLabel={setJobTitleLabel}
            jobRequisition={jobRequisition}
            setJobRequisition={setJobRequisition}
            jobDescription={jobDescription}
            setJobDescription={setJobDescription}
            autoSuitability={autoSuitability}
            setAutoSuitability={setAutoSuitability}
            autoOutstanding={autoOutstanding}
            setAutoOutstanding={setAutoOutstanding}
            autoAssessment={autoAssessment}
            setAutoAssessment={setAutoAssessment}
            jobPostingData={jobPostingData}
            setJobPostingData={setJobPostingData}
            jobPostExist={jobPostExist}
          />
        );
      case 'Auto-Assessments':
        return <AssessmentQuestions />;
      case 'Applicant Analysis':
        return <AllApplicantAnalysis jobOpeningElements={jobOpeningElements} />;
      case 'Smart Interviews':
        return <Interviews />;
      case 'Wellness':
        return <WellnessTab jobOpeningId={jobOpeningId ?? ''} />;
      case 'Psychometric':
        return <Psychometric />;
      default:
        return (
          <StatusMessage
            status="disabled"
            requiredTabs={[]}
            enabledElement={async () => {}}
            setCurrentPage={setCurrentPage}
            setStatusTab={navigateToSubTab}
          />
        );
    }
  };

  return (
    <JobOpeningsContext.Provider
      value={{
        jobOpeningElements,
        setJobOpeningElements,
        completedJobOpeningDetails,
        setCompletedJobOpeningDetails,
        jobOpeningId,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          width: '100%',
          maxWidth: '100%',
          overflowX: 'hidden',
          borderRadius: 2,
          border: 1,
          borderColor: 'grey.100',
          bgcolor: 'background.paper',
        }}
      >
        <Box sx={{ width: '100%', maxWidth: '100%', overflowX: 'hidden', bgcolor: 'background.paper' }}>
          {!modalState ? (
            <>
              <JobOpeningBreadcrumb
                jobTitle={jobTitleLabel}
                jobReferenceNumber={String(jobDescription?.jobReferenceNumber || '')}
                jobOpeningId={jobOpeningId || ''}
              />

              <Box sx={{ px: 2, py: 1.5 }}>
                <ProgressTracker
                  title={jobTitleLabel || t(ADMIN_PAGES_KEYS.jobOpenings.nav.newJobOpening)}
                  globalState="ON GOING"
                  onClose={() => setShow(false)}
                  show={show}
                  setShow={setShow}
                  stages={stages}
                  setStages={setStages}
                  jobOpeningId={jobOpeningId}
                />
              </Box>

              <JobOpeningTabs
                steps={steps}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                counts={tabCounts}
              />

              {renderFormPage()}
            </>
          ) : (
            <>
              <CreateJobDialog
                open={modalState}
                onClose={() => setConfirmDialogState(true)}
                onSuccessClose={() => setModalState(false)}
              />
              <Dialog
                title={t(ADMIN_PAGES_KEYS.jobOpenings.cancelDialog.title)}
                handleApply={handleModalToggle}
                confirmButtonLable={t(ADMIN_PAGES_KEYS.jobOpenings.cancelDialog.confirm)}
                isOpen={confirmDialogState}
                onClose={() => setConfirmDialogState(false)}
                cancelButtonLable={t(ADMIN_PAGES_KEYS.jobOpenings.cancelDialog.stay)}
                dialogType={DIALOG_TYPE.WARNING}
                justify="end"
              >
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {t(ADMIN_PAGES_KEYS.jobOpenings.cancelDialog.message)}
                </Typography>
              </Dialog>
            </>
          )}
        </Box>
      </Paper>
    </JobOpeningsContext.Provider>
  );
};
